import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { API_TAGS } from '@/shared/api';
import {
  createAttributeGroup,
  deleteAttributeGroup,
  fetchAllAttributeGroups,
  fetchAttributeGroupByCode,
  fetchAttributeGroups,
  updateAttributeGroup,
} from './attributeGroupApi';
import type {
  AttributeGroupCreate,
  AttributeGroupDeleteParams,
  AttributeGroupListParams,
  AttributeGroupUpdate,
} from './attributeGroupTypes';

export const attributeGroupKeys = {
  all: [API_TAGS.ATTRIBUTE_GROUPS] as const,
  lists: () => [...attributeGroupKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown> = {}) => [...attributeGroupKeys.lists(), { ...filters }] as const,
  fullList: () => [...attributeGroupKeys.lists(), 'full'] as const,
  details: () => [...attributeGroupKeys.all, 'detail'] as const,
  detail: (code: string) => [...attributeGroupKeys.details(), code] as const,
};

export const useAttributeGroups = (params?: AttributeGroupListParams) => {
  return useQuery({
    queryKey: attributeGroupKeys.list(params),
    queryFn: () => fetchAttributeGroups(params),
  });
};

/**
 * Хук всего набора групп атрибутов: набор доходит до последней группы, сколько бы окон
 * выдачи он ни занимал. Нужен тем, кто раскладывает атрибуты по группам целиком, — вкладкам
 * и списку раздела атрибутов, данным диалога: первое окно выдачи за весь набор им выдавать
 * нельзя, а расходиться между собой они не должны.
 */
export const useAllAttributeGroups = () => {
  return useQuery({
    queryKey: attributeGroupKeys.fullList(),
    queryFn: () => fetchAllAttributeGroups(),
  });
};

export const useInfinityAttributeGroups = (params: AttributeGroupListParams) => {
  const limit = params.limit || 50;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: attributeGroupKeys.list(params),
    queryFn: ({ pageParam }) => fetchAttributeGroups({ ...params, limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.total <= limit * (lastPageParam + 1) ? undefined : lastPageParam + 1;
    },
  });

  const data = useMemo(
    () => ({
      total: query.data?.pages[0]?.total,
      attributeGroups: query.data?.pages.flatMap((p) => p.attributeGroups),
    }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

export const useAttributeGroup = (code?: string) => {
  return useQuery({
    queryKey: attributeGroupKeys.detail(code!),
    queryFn: () => fetchAttributeGroupByCode(code!),
    enabled: !!code,
  });
};

export const useCreateAttributeGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AttributeGroupCreate) => createAttributeGroup(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.lists() });
    },
  });
};

export const useDeleteAttributeGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code, params }: { code: string; params?: AttributeGroupDeleteParams }) =>
      deleteAttributeGroup(code, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.lists() });
    },
  });
};

export const useUpdateAttributeGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { code: string; body: AttributeGroupUpdate }) => updateAttributeGroup(params),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.detail(variables.code) });
      queryClient.invalidateQueries({ queryKey: attributeGroupKeys.lists() });
    },
  });
};
